import type { Location as DomainLocation } from '#domain/Location.ts';
import type { Material } from '#domain/Material.ts';
import type { TransportOrder } from '#domain/TransportOrder.ts';
import { parseGeoCoordinateNumber } from '#utils/geoParsing.util.ts';

import type {
    BulkMaterialFormData,
    CargoMaterialFormData,
    Location as FormLocation,
    MaterialFormData,
    OrderFormData,
    TransportFormData,
    UpdateOrderFormData,
} from './OrderFormValidation.ts';
import { splitDateTimeValue } from './timeWindowFields.ts';

export const initialMaterial: MaterialFormData = {
    quantity: 0,
    type: 'cargo-material',
    name: '',
    packageType: 'unspecified',
};

export const emptyMaterial = (material: MaterialFormData): MaterialFormData => {
    switch (material.type) {
        case 'cargo-material':
            return {
                quantity: 0,
                type: material.type,
                name: '',
                packageType: material.packageType,
                index: 0,
            };
        case 'bulk-material':
            return {
                quantity: 0,
                type: material.type,
                name: '',
                unitType: material.unitType,
                packageType: material.packageType,
                index: 0,
            };
    }
};

export const initialFormData: TransportFormData = {
    page: 'loadingStop',
    id: undefined,
    number: '',
    customerName: '',
    loadingComment: '',
    unloadingComment: '',
    loadingStop: {
        name: '',
        location: {
            street: '',
            city: '',
            country: '',
            zipCode: '',
        },
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        type: 'load',
    },
    unloadingStop: {
        name: '',
        location: {
            street: '',
            city: '',
            country: '',
            zipCode: '',
        },
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        type: 'unload',
    },
    materials: [],
};

export const mapMaterialFormData = (material: Material, index: number): MaterialFormData => {
    switch (material.type) {
        case 'cargo-material':
            return {
                id: material.id,
                type: 'cargo-material',
                quantity: material.quantity.target,
                packageType: material.packageType,
                loadingMeter: material.loadingMeter?.target,
                index: index,
                name: material.name,
                weight: material.weight?.target,
                length: material.length,
                width: material.width,
                height: material.height,
            } satisfies CargoMaterialFormData;
        case 'bulk-material':
            return {
                id: material.id,
                type: 'bulk-material',
                quantity: material.quantity.target,
                packageType: material.packageType,
                unitType: material.unitType,
                name: material.name,
            } satisfies BulkMaterialFormData;
    }
};

const mapLocation = (location: DomainLocation): FormLocation => ({
    city: location.city,
    street: location.street,
    country: location.country,
    zipCode: location.zipCode,
});

export const mapOrderToFormData = (order: TransportOrder, materials: Material[]): UpdateOrderFormData => {
    const loadingStartTarget = splitDateTimeValue(order.loadingStop.startTarget);
    const loadingEndTarget = splitDateTimeValue(order.loadingStop.endTarget);
    const unloadingStartTarget = splitDateTimeValue(order.unloadingStop.startTarget);
    const unloadingEndTarget = splitDateTimeValue(order.unloadingStop.endTarget);

    return {
        page: 'loadingStop',
        id: order.id,
        number: order.number,
        customerName: order.customerName,
        loadingComment: order.loadingComment,
        unloadingComment: order.unloadingComment,
        loadingStop: {
            id: order.loadingStop.id,
            name: order.loadingStop.name,
            location: mapLocation(order.loadingStop.location),
            startDate: loadingStartTarget.date,
            startTime: loadingStartTarget.time,
            endDate: loadingEndTarget.date,
            endTime: loadingEndTarget.time,
            latitude: parseGeoCoordinateNumber(order.loadingStop.latitude),
            longitude: parseGeoCoordinateNumber(order.loadingStop.longitude),
            type: 'load',
        },
        unloadingStop: {
            id: order.unloadingStop.id,
            name: order.unloadingStop.name,
            location: mapLocation(order.unloadingStop.location),
            startDate: unloadingStartTarget.date,
            startTime: unloadingStartTarget.time,
            endDate: unloadingEndTarget.date,
            endTime: unloadingEndTarget.time,
            latitude: parseGeoCoordinateNumber(order.unloadingStop.latitude),
            longitude: parseGeoCoordinateNumber(order.unloadingStop.longitude),
            type: 'unload',
        },
        materials: materials.map(mapMaterialFormData),
    };
};

export const createMaterialDuplicate = (material: Material): MaterialFormData => {
    switch (material.type) {
        case 'cargo-material':
            return {
                type: 'cargo-material',
                quantity: material.quantity.target,
                packageType: material.packageType,
                loadingMeter: material.loadingMeter?.target,
                name: material.name,
                weight: material.weight?.target,
                index: material.index ?? undefined,
                length: material.length ?? undefined,
                width: material.width ?? undefined,
                height: material.height ?? undefined,
            } satisfies CargoMaterialFormData;
        case 'bulk-material':
            return {
                type: 'bulk-material',
                quantity: material.quantity.target,
                packageType: material.packageType,
                unitType: material.unitType,
                name: material.name,
                index: material.index ?? undefined,
            } satisfies BulkMaterialFormData;
    }
};

export const createOrderDuplicate = (order: TransportOrder, materials: Material[]): OrderFormData => ({
    page: 'loadingStop',
    number: '',
    customerName: order.customerName,
    loadingComment: order.loadingComment,
    unloadingComment: order.unloadingComment,
    loadingStop: {
        name: order.loadingStop.name,
        location: mapLocation(order.loadingStop.location),
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        latitude: parseGeoCoordinateNumber(order.loadingStop.latitude),
        longitude: parseGeoCoordinateNumber(order.loadingStop.longitude),
        type: 'load',
    },
    unloadingStop: {
        name: order.unloadingStop.name,
        location: mapLocation(order.unloadingStop.location),
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        latitude: parseGeoCoordinateNumber(order.unloadingStop.latitude),
        longitude: parseGeoCoordinateNumber(order.unloadingStop.longitude),
        type: 'unload',
    },
    materials: materials.map(createMaterialDuplicate),
});
